import {
  TrendingUp,
  Clock,
  Heart,
  Flame,
  ChevronRight,
  Info,
} from "lucide-react";
import { message } from "antd";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Button } from "../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  ReferenceLine,
} from "recharts";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService from "../../services/HomeService";
import mealApiService from "../../services/MealApiService";

// Homepage Components
import NutritionSummaryCard from "../../components/HomePage/NutritionSummaryCard";
import GutHealthScoreCard from "../../components/HomePage/GutHealthScoreCard";

const HomePage = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(
    JSON.parse(localStorage.getItem("userProfile") || "{}")
  );

  // Fetch home page data with React Query
  const {
    data: homeData,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
    staleTime: 0, // Always fetch fresh data for auto-refresh
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true, // Auto refetch on focus
  });

  // Fasting Timer States
  const [isFasting, setIsFasting] = useState(false);
  const [fastingStartTime, setFastingStartTime] = useState<number | null>(null);
  const [fastingDuration, setFastingDuration] = useState(0);
  const [lastCompletedDuration, setLastCompletedDuration] = useState<string | null>(
    localStorage.getItem("lastCompletedDuration")
  );

  // Meal Goals State
  const [mealGoals, setMealGoals] = useState({
    calories_target: 2000,
    calories_goal_burned: 600, // Daily calorie burn goal
  });

  // Load fasting state from localStorage and sync with API data
  useEffect(() => {
    const savedFastingState = localStorage.getItem("fastingState");
    const savedStartTime = localStorage.getItem("fastingStartTime");

    if (savedFastingState === "true" && savedStartTime) {
      setIsFasting(true);
      setFastingStartTime(parseInt(savedStartTime));
    }
  }, []);

  // Sync fasting state with API data when homeData loads
  useEffect(() => {
    // Check localStorage first
    const savedFastingState = localStorage.getItem("fastingState");
    const savedStartTime = localStorage.getItem("fastingStartTime");

    if (savedFastingState === "true" && savedStartTime) {
      // If we have local state saying we're fasting, trust it first
      // This prevents the timer from disappearing while waiting for API sync
      setIsFasting(true);
      setFastingStartTime(parseInt(savedStartTime));
    }

    if (homeData?.["Intermittent Fasting"]) {
      const fastingData = homeData["Intermittent Fasting"];

      // Check 3 conditions: Status = "Fasting" AND Elapsed Time UI AND Elapsed Seconds
      const isApiFasting =
        fastingData.Status === "Fasting" &&
        fastingData["Elapsed Time UI"] &&
        fastingData["Elapsed Seconds"] !== null;

      if (
        isApiFasting &&
        fastingData["Time Started"] &&
        fastingData["Elapsed Seconds"] !== null
      ) {
        // Calculate start time from elapsed seconds
        const now = Date.now();
        const elapsedMilliseconds = fastingData["Elapsed Seconds"] * 1000;
        const startTime = now - elapsedMilliseconds;

        // Check for zombie session (started before last explicit stop)
        const lastStoppedTime = localStorage.getItem("lastStoppedTime");
        if (lastStoppedTime && startTime < parseInt(lastStoppedTime)) {
          // Ignoring stale API data that conflicts with explicit stop
          return;
        }

        // Only update if not already set or if significant difference
        if (
          !savedStartTime ||
          Math.abs(parseInt(savedStartTime) - startTime) > 5000
        ) {
          setIsFasting(true);
          setFastingStartTime(startTime);
          localStorage.setItem("fastingState", "true");
          localStorage.setItem("fastingStartTime", startTime.toString());
          // Clear stop time if we legitimately start a new timer
          if (lastStoppedTime) {
            localStorage.removeItem("lastStoppedTime");
          }
        }
      } else if (!isApiFasting && savedFastingState !== "true") {
        // Only turn off if API says off AND local storage says off/empty
        setIsFasting(false);
        setFastingStartTime(null);
        localStorage.setItem("fastingState", "false");
        localStorage.removeItem("fastingStartTime");
      }
    }
  }, [homeData]);

  // Update fasting duration every second when fasting is active
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isFasting && fastingStartTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const duration = Math.floor((now - fastingStartTime) / 1000); // Duration in seconds
        setFastingDuration(duration);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isFasting, fastingStartTime]);

  // Fetch meal goals for today's date
  // Define fetchMealGoals as a reusable function
  const fetchMealGoals = async () => {
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const dateString = `${year}-${month}-${day}`;

      const response = await mealApiService.getMealDataByDate(dateString);
      const actualData = response?.data ?? response;

      if (actualData?.meal_goals) {
        setMealGoals({
          calories_target: actualData.meal_goals.calories_target ?? 2000,
          calories_goal_burned:
            actualData.meal_goals.calories_goal_burned ?? 600,
        });
      }
    } catch (error) {
      // Keep default values on error
    }
  };

  // Refresh all data function
  const refreshAllData = () => {
    refetch(); // Refresh HomaPage API data
    fetchMealGoals(); // Refresh Meal Goals
    // Refresh user profile from local storage
    setUserProfile(JSON.parse(localStorage.getItem("userProfile") || "{}"));
  };

  // Initial load
  useEffect(() => {
    fetchMealGoals();
  }, []);

  // Consolidated Auto-Refresh Logic
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshAllData(); // Refresh ALL data when visible
      }
    };

    const handleFocus = () => {
      refreshAllData(); // Refresh ALL data on focus
    };

    const handleMedicationUpdate = () => {
      refreshAllData(); // Refresh ALL data on medication update
    };

    const handleStorageChange = (e: StorageEvent) => {
      // Refresh when profile or preferences are updated
      if (e.key === "userProfile" || e.key === "user_preferences" ||
        e.key === "glucera_weight_unit" || e.key === "glucera_height_unit") {
        refetch(); // Refetch home page data to get updated BMI
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("medicationsUpdated", handleMedicationUpdate);
    window.addEventListener("storage", handleStorageChange);

    // Periodic refresh every 5 minutes
    const interval = setInterval(
      () => {
        refreshAllData();
      },
      5 * 60 * 1000
    );

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("medicationsUpdated", handleMedicationUpdate);
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [refetch]);

  // Format duration to display (hours, minutes, seconds)
  const formatFastingTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Start fasting function with API call
  const startFasting = async () => {
    try {
      await homeService.startFasting();

      const startTime = Date.now();
      setIsFasting(true);
      setFastingStartTime(startTime);
      setFastingDuration(0);

      // Save to localStorage
      localStorage.setItem("fastingState", "true");
      localStorage.setItem("fastingStartTime", startTime.toString());
    } catch (error) {
      // Silently handle error - UI will still update
    }
  };

  // End fasting function with API call
  const endFasting = async () => {
    try {
      await homeService.stopFasting();

      // Reload food-home-page to get updated data with Duration UI
      const updatedData = await refetch();

      // Get Duration UI from the refreshed API data
      const durationUI = updatedData.data?.["Intermittent Fasting"]?.["Duration UI"];

      if (durationUI) {
        // Save to state and localStorage
        setLastCompletedDuration(durationUI);
        localStorage.setItem("lastCompletedDuration", durationUI);

        message.success(`Fasting completed! Duration: ${durationUI}`);
      }
    } catch (error: any) {
      // If no active session found, that's okay - just continue to update UI
    }

    // Always update UI state regardless of API response
    setIsFasting(false);
    setFastingStartTime(null);
    setFastingDuration(0);

    // Clear localStorage
    localStorage.removeItem("fastingState");
    localStorage.removeItem("fastingStartTime");
    // Save stop time to prevent zombie timer
    localStorage.setItem("lastStoppedTime", Date.now().toString());
  };

  // Gut Health Score with sub-scores - now from API
  const foodScore = homeData?.["Food GHI"]?.daily_ghi || 0;
  const lifestyleScore =
    homeData?.["Lifestyle Synergy"]?.lifestyle_score || 0;

  // Component data objects
  const nutritionData = {
    calories: {
      current: homeData?.["Today's Nutrition"]?.["Total Calories"] || 0,
      target: mealGoals.calories_target,
    },
    protein: homeData?.["Today's Nutrition"]?.["Total Proteins"] || 0,
    carbs: homeData?.["Today's Nutrition"]?.["Total Carbohydrates"] || 0,
    fat: homeData?.["Today's Nutrition"]?.["Total Fats"] || 0,
    fiber: homeData?.["Today's Nutrition"]?.["Total Fiber"] || 0,
  };

  // Weekly calorie data - combine intake and burned
  const weeklyCalorieData = (() => {
    const intakeTrend = homeData?.["Weekly Calorie Intake Trend"]?.graph || [];
    const burnedTrend = homeData?.["Weekly Calorie Burned Trend"]?.graph || [];

    // Group intake by day and sum values
    const intakeByDay = intakeTrend.reduce((acc: any, item: any) => {
      if (!acc[item.day]) {
        acc[item.day] = 0;
      }
      acc[item.day] += item.value;
      return acc;
    }, {});

    // Create combined array
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return days.map(day => ({
      day,
      intake: intakeByDay[day] || 0,
      burned: burnedTrend.find((b: any) => b.day === day)?.value || 0,
    }));
  })();

  // Weekly sleep data - convert minutes to hours
  const weeklySleepData = (() => {
    const sleepTrend = homeData?.["Weekly Sleep Trend"]?.graph || [];
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return days.map(day => ({
      day,
      hours: (sleepTrend.find((s: any) => s.day === day)?.value || 0) / 60, // Convert minutes to hours
      baseline: 0, // For axis dots styling
    }));
  })();

  // Show loading state while fetching data
  if (loading) {
    return (
      <div
        className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
        style={{
          minHeight: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header with User Greeting */}
        <div className="pb-1 pt-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Welcome ,
          </h2>
          <h1 className="text-2xl font-semibold text-foreground">
            {homeData?.user || userProfile.name || ""}
          </h1>
        </div>

        {/* Gut Health Score Card (Bite Balance) */}
        <div className="h-56">

          <GutHealthScoreCard
            foodScore={foodScore}
            foodMaxScore={80}
            lifestyleScore={lifestyleScore}
            lifestyleMaxScore={20}
          />
        </div>

        {/* Insights Card */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-foreground">
              Health Insights
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Carb-Fiber Ratio */}
            <Card
              className="cursor-pointer rounded-xl border border-border bg-card/50 p-4 transition-shadow hover:shadow-md"
              onClick={() =>
                navigate("/glucera" + GluceraNavigationRoutes.CARB_FIBER)
              }
            >
              <div className="mb-2 flex items-center gap-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border-2 ${homeData?.["Health Insights"]?.carb_fiber_ratio_daily
                    ?.label === "Good" ||
                    homeData?.["Health Insights"]?.carb_fiber_ratio_daily
                      ?.label === "Excellent"
                    ? "border-green-300 bg-green-100 dark:border-green-700 dark:bg-green-900/30"
                    : homeData?.["Health Insights"]?.carb_fiber_ratio_daily
                      ?.label === "Needs Improvement"
                      ? "border-orange-300 bg-orange-100 dark:border-orange-700 dark:bg-orange-900/30"
                      : "border-red-300 bg-red-100 dark:border-red-700 dark:bg-red-900/30"
                    }`}
                >
                  <TrendingUp
                    className={`h-4 w-4 ${homeData?.["Health Insights"]?.carb_fiber_ratio_daily
                      ?.label === "Good" ||
                      homeData?.["Health Insights"]?.carb_fiber_ratio_daily
                        ?.label === "Excellent"
                      ? "text-green-500"
                      : homeData?.["Health Insights"]?.carb_fiber_ratio_daily
                        ?.label === "Needs Improvement"
                        ? "text-orange-500"
                        : "text-red-500"
                      }`}
                  />
                </div>
              </div>
              <div className="mb-1 text-lg font-bold text-foreground">
                {homeData?.["Health Insights"]?.carb_fiber_ratio_daily?.label || "--"}
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                Carb-Fiber Ratio
              </p>
            </Card>

            {/* Fat Quality */}
            <Card
              className="cursor-pointer rounded-xl border border-border bg-card/50 p-4 transition-shadow hover:shadow-md"
              onClick={() =>
                navigate("/glucera" + GluceraNavigationRoutes.FAT_QUALITY)
              }
            >
              <div className="mb-2 flex items-center gap-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border-2 ${homeData?.["Health Insights"]?.bad_fat_ratio_daily?.label ===
                    "Good" ||
                    homeData?.["Health Insights"]?.bad_fat_ratio_daily?.label ===
                    "Excellent"
                    ? "border-blue-300 bg-blue-100 dark:border-blue-700 dark:bg-blue-900/30"
                    : homeData?.["Health Insights"]?.bad_fat_ratio_daily
                      ?.label === "Needs Improvement"
                      ? "border-orange-300 bg-orange-100 dark:border-orange-700 dark:bg-orange-900/30"
                      : "border-red-300 bg-red-100 dark:border-red-700 dark:bg-red-900/30"
                    }`}
                >
                  <Heart
                    className={`h-4 w-4 ${homeData?.["Health Insights"]?.bad_fat_ratio_daily
                      ?.label === "Good" ||
                      homeData?.["Health Insights"]?.bad_fat_ratio_daily
                        ?.label === "Excellent"
                      ? "text-blue-500"
                      : homeData?.["Health Insights"]?.bad_fat_ratio_daily
                        ?.label === "Needs Improvement"
                        ? "text-orange-500"
                        : "text-red-500"
                      }`}
                  />
                </div>
              </div>
              <div className="mb-1 text-lg font-bold text-foreground">
                {homeData?.["Health Insights"]?.bad_fat_ratio_daily?.label || "--"}
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                Fat Quality
              </p>
            </Card>

            {/* Sugar Load */}
            <Card
              className="cursor-pointer rounded-xl border border-border bg-card/50 p-4 transition-shadow hover:shadow-md"
              onClick={() =>
                navigate("/glucera" + GluceraNavigationRoutes.SUGAR_LOAD)
              }
            >
              <div className="mb-2 flex items-center gap-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border-2 ${homeData?.["Health Insights"]?.sugar_ratio_daily?.label ===
                    "Good" ||
                    homeData?.["Health Insights"]?.sugar_ratio_daily?.label ===
                    "Excellent"
                    ? "border-green-300 bg-green-100 dark:border-green-700 dark:bg-green-900/30"
                    : homeData?.["Health Insights"]?.sugar_ratio_daily
                      ?.label === "Needs Improvement" ||
                      homeData?.["Health Insights"]?.sugar_ratio_daily
                        ?.label === "Moderate"
                      ? "border-orange-300 bg-orange-100 dark:border-orange-700 dark:bg-orange-900/30"
                      : "border-red-300 bg-red-100 dark:border-red-700 dark:bg-red-900/30"
                    }`}
                >
                  <Flame
                    className={`h-4 w-4 ${homeData?.["Health Insights"]?.sugar_ratio_daily?.label ===
                      "Good" ||
                      homeData?.["Health Insights"]?.sugar_ratio_daily?.label ===
                      "Excellent"
                      ? "text-green-500"
                      : homeData?.["Health Insights"]?.sugar_ratio_daily
                        ?.label === "Needs Improvement" ||
                        homeData?.["Health Insights"]?.sugar_ratio_daily
                          ?.label === "Moderate"
                        ? "text-orange-500"
                        : "text-red-500"
                      }`}
                  />
                </div>
              </div>
              <div className="mb-1 text-lg font-bold text-foreground">
                {homeData?.["Health Insights"]?.sugar_ratio_daily?.label || "--"}
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                Sugar Load
              </p>
            </Card>

            {/* Protein-Fiber Ratio */}
            <Card
              className="cursor-pointer rounded-xl border border-border bg-card/50 p-4 transition-shadow hover:shadow-md"
              onClick={() =>
                navigate(
                  "/glucera" + GluceraNavigationRoutes.METABOLIC_STABILITY
                )
              }
            >
              <div className="mb-2 flex items-center gap-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border-2 ${homeData?.["Health Insights"]?.protein_fiber_ratio_daily
                    ?.label === "Good" ||
                    homeData?.["Health Insights"]?.protein_fiber_ratio_daily
                      ?.label === "Excellent"
                    ? "border-green-300 bg-green-100 dark:border-green-700 dark:bg-green-900/30"
                    : homeData?.["Health Insights"]?.protein_fiber_ratio_daily
                      ?.label === "Needs Improvement" ||
                      homeData?.["Health Insights"]?.protein_fiber_ratio_daily
                        ?.label === "Needs Attention"
                      ? "border-orange-300 bg-orange-100 dark:border-orange-700 dark:bg-orange-900/30"
                      : "border-red-300 bg-red-100 dark:border-red-700 dark:bg-red-900/30"
                    }`}
                >
                  <TrendingUp
                    className={`h-4 w-4 ${homeData?.["Health Insights"]?.protein_fiber_ratio_daily
                      ?.label === "Good" ||
                      homeData?.["Health Insights"]?.protein_fiber_ratio_daily
                        ?.label === "Excellent"
                      ? "text-green-500"
                      : homeData?.["Health Insights"]?.protein_fiber_ratio_daily
                        ?.label === "Needs Improvement" ||
                        homeData?.["Health Insights"]?.protein_fiber_ratio_daily
                          ?.label === "Needs Attention"
                        ? "text-orange-500"
                        : "text-red-500"
                      }`}
                  />
                </div>
              </div>
              <div className="mb-1 text-lg font-bold text-foreground">
                {homeData?.["Health Insights"]?.protein_fiber_ratio_daily?.label || "--"}
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                Protein-Fiber Ratio
              </p>
            </Card>
          </div>
        </Card>

        {/* Intermittent Fasting Card */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-green-300 bg-green-100 dark:border-green-700 dark:bg-green-900/30">
                <Clock className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-foreground">
                    Intermittent Fasting
                  </h3>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 rounded-full hover:bg-green-100"
                      >
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-4" align="center" side="bottom">
                      <div className="space-y-4">
                        <div>
                          <h4 className="mb-3 text-sm font-semibold text-foreground">
                            Gut Health Benefits:
                          </h4>
                          <div className="space-y-2.5">
                            <div className="flex items-start gap-2.5">
                              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-600" />
                              <div>
                                <p className="mb-0.5 text-xs font-semibold text-foreground">
                                  MMC Activation
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Enables gut cleansing waves to clear bacteria
                                  and debris
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2.5">
                              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-600" />
                              <div>
                                <p className="mb-0.5 text-xs font-semibold text-foreground">
                                  Reduced Inflammation
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Gut lining heals and inflammatory responses
                                  decrease
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2.5">
                              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-600" />
                              <div>
                                <p className="mb-0.5 text-xs font-semibold text-foreground">
                                  Microbiome Balance
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Promotes beneficial bacteria growth and
                                  diversity
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2.5">
                              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-600" />
                              <div>
                                <p className="mb-0.5 text-xs font-semibold text-foreground">
                                  Insulin Sensitivity
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Better blood sugar regulation reduces gut
                                  stress
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="mb-3 text-sm font-semibold text-foreground">
                            Fasting Windows:
                          </h4>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between rounded-lg bg-muted/30 p-2">
                              <span className="text-xs font-medium text-foreground">
                                12-14 hours
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Beginner
                              </span>
                            </div>
                            <div className="flex items-center justify-between rounded-lg bg-muted/30 p-2">
                              <span className="text-xs font-medium text-foreground">
                                14-16 hours
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Optimal
                              </span>
                            </div>
                            <div className="flex items-center justify-between rounded-lg bg-muted/30 p-2">
                              <span className="text-xs font-medium text-foreground">
                                16-18 hours
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Advanced
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <p className="text-xs font-medium text-green-600">18:6 Goal</p>
              </div>
            </div>
          </div>

          <div className="mb-4 text-center">
            <div className="mb-2 text-lg font-bold text-blue-600">
              {homeData?.["Intermittent Fasting"]?.Status}
            </div>
            <p className="mb-1 text-xs text-muted-foreground">
              {homeData?.["Intermittent Fasting"]?.Status === "Fasting"
                ? "Current fasting time"
                : "Last fasting window"}
            </p>
            <div className="mb-3 text-2xl font-bold text-green-600">
              {homeData?.["Intermittent Fasting"]?.["Fasting Time UI"] ||
                homeData?.["Intermittent Fasting"]?.["Duration UI"] ||
                "0m 0s"}
            </div>
            {homeData?.["Intermittent Fasting"]?.["Fasting Goal"] && (
              <p className="text-xs font-medium text-green-600">
                Goal: {homeData["Intermittent Fasting"]["Fasting Goal"]}
              </p>
            )}
          </div>
        </Card>
        {/* Today's Nutrition Summary */}
        <NutritionSummaryCard nutrition={nutritionData} />

        {/* Activity Summary */}
        <div
          className="cursor-pointer transition-transform hover:scale-[1.02]"
          onClick={() => navigate("/glucera/fitness")}
        >
          <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                Today's Activity
              </h3>
              {/* <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs font-medium text-foreground/70 hover:bg-accent hover:text-foreground"
              onClick={() => navigate("/glucera/fitness")}
            >
              View All <ChevronRight className="ml-0.5 h-4 w-4" />
            </Button> */}
            </div>

            {/* Calories Burned Section */}
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-orange-300 bg-orange-100 dark:border-orange-700 dark:bg-orange-900/30">
                <Flame className="h-5 w-5 text-orange-500" />
              </div>
              <div className="flex-1">
                <div className="mb-1 flex items-baseline gap-1">
                  <span className="text-xl font-bold text-foreground">
                    {homeData?.["Today's Activity"]?.["Calories Burned"] || 0}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    /{" "}
                    {homeData?.["Today's Goals"]?.["Total Activity Goal"] ||
                      mealGoals.calories_goal_burned}{" "}
                    cal
                  </span>
                  <span className="ml-auto text-sm font-medium text-foreground">
                    {Math.round(
                      ((homeData?.["Today's Activity"]?.["Calories Burned"] ||
                        0) /
                        (homeData?.["Today's Goals"]?.["Total Activity Goal"] ||
                          mealGoals.calories_goal_burned)) *
                      100
                    )}
                    %
                  </span>
                </div>
                <p className="mb-2 text-xs text-muted-foreground">
                  Calories Burned
                </p>
                <Progress
                  value={Math.round(
                    ((homeData?.["Today's Activity"]?.["Calories Burned"] ||
                      0) /
                      (homeData?.["Today's Goals"]?.["Total Activity Goal"] ||
                        mealGoals.calories_goal_burned)) *
                    100
                  )}
                  className="h-2"
                />
              </div>
            </div>

            {/* Activity Metrics Grid */}
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center">
                <div className="text-lg font-bold text-blue-500">
                  {homeData?.["Today's Activity"]?.[
                    "Total Steps"
                  ]?.toLocaleString() || 0}
                </div>
                <div className="text-xs text-muted-foreground">Steps</div>
              </div>

              <div className="text-center">
                <div className="text-lg font-bold text-green-500">
                  {homeData?.["Today's Activity"]?.BPM || 0}
                </div>
                <div className="text-xs text-muted-foreground">BPM</div>
              </div>

              <div className="text-center">
                <div className="text-lg font-bold text-orange-500">
                  {homeData?.["Today's Activity"]?.Sleep || 0}h
                </div>
                <div className="text-xs text-muted-foreground">Sleep</div>
              </div>

              <div className="text-center">
                <div className="text-lg font-bold text-purple-500">
                  {homeData?.["Today's Activity"]?.["Active Minutes"] || 0}
                </div>
                <div className="text-xs text-muted-foreground">Active Min</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Water Intake */}
        {/* <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg border-2 border-blue-500 bg-blue-100 dark:bg-blue-900/30 dark:border-blue-700">
                <Droplets className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Water Intake
                </h3>
                <p className="text-xs text-muted-foreground">6 of 8 glasses</p>
              </div>
            </div>
            <Button
              size="sm"
              className="h-9 rounded-lg bg-blue-500 text-xs font-medium text-white transition-all duration-200 hover:bg-blue-600 hover:shadow-md"
            >
              Log
            </Button>
          </div>
        </Card> */}

        {/* BMI Card */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-bold text-foreground">
              Body Mass Index (BMI)
            </h3>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 border-2 p-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-bold">How BMI is Calculated</h4>
                  <p className="text-xs font-semibold text-muted-foreground">
                    BMI = weight (kg) / height² (m²)
                  </p>
                  <div className="space-y-2 text-xs">
                    <p className="font-bold">BMI Categories:</p>
                    <div className="space-y-1 font-semibold text-muted-foreground">
                      <p>• Underweight: &lt; 18.5</p>
                      <p>• Normal weight: 18.5 - 24.9</p>
                      <p>• Overweight: 25 - 29.9</p>
                      <p>• Obese: ≥ 30</p>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="mb-2 text-5xl font-bold text-blue-500">
                {homeData?.["Body Mass Index"]?.BMI?.toFixed(1) || "0"}
              </div>
              <div
                className="inline-block rounded-xl border-2 px-4 py-2"
                style={{
                  borderColor:
                    homeData?.["Body Mass Index"]?.["BMI Category Color"] ===
                      "red"
                      ? "rgba(220, 38, 38, 0.3)"
                      : homeData?.["Body Mass Index"]?.[
                        "BMI Category Color"
                      ] === "yellow"
                        ? "rgba(202, 138, 4, 0.3)"
                        : homeData?.["Body Mass Index"]?.[
                          "BMI Category Color"
                        ] === "orange"
                          ? "rgba(234, 88, 12, 0.3)"
                          : "rgba(22, 163, 74, 0.3)",
                  backgroundColor:
                    homeData?.["Body Mass Index"]?.["BMI Category Color"] ===
                      "red"
                      ? "rgba(220, 38, 38, 0.1)"
                      : homeData?.["Body Mass Index"]?.[
                        "BMI Category Color"
                      ] === "yellow"
                        ? "rgba(202, 138, 4, 0.1)"
                        : homeData?.["Body Mass Index"]?.[
                          "BMI Category Color"
                        ] === "orange"
                          ? "rgba(234, 88, 12, 0.1)"
                          : "rgba(22, 163, 74, 0.1)",
                }}
              >
                <p
                  className="text-sm font-bold"
                  style={{
                    color:
                      homeData?.["Body Mass Index"]?.["BMI Category Color"] ===
                        "red"
                        ? "#dc2626"
                        : homeData?.["Body Mass Index"]?.[
                          "BMI Category Color"
                        ] === "yellow"
                          ? "#ca8a04"
                          : homeData?.["Body Mass Index"]?.[
                            "BMI Category Color"
                          ] === "orange"
                            ? "#ea580c"
                            : "#16a34a",
                  }}
                >
                  {homeData?.["Body Mass Index"]?.["BMI Category"] || "0"}
                </p>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-muted-foreground">
                {(homeData?.["Body Mass Index"]?.["Height ft"] || homeData?.["Body Mass Index"]?.["Height cm"]) && (
                  <p>
                    {homeData?.["Body Mass Index"]?.["Height ft"] ? (
                      <>
                        Height: {homeData["Body Mass Index"]["Height ft"]}{" "}
                        {homeData?.["Body Mass Index"]?.["Height cm"] && (
                          <>({Math.round(homeData["Body Mass Index"]["Height cm"])} cm)</>
                        )}
                      </>
                    ) : (
                      <>Height: {Math.round(homeData["Body Mass Index"]["Height cm"])} cm</>
                    )}
                  </p>
                )}
                {(homeData?.["Body Mass Index"]?.["Weight lbs"] || homeData?.["Body Mass Index"]?.["Weight kg"]) && (
                  <p>
                    {homeData?.["Body Mass Index"]?.["Weight lbs"] ? (
                      <>
                        Weight: {homeData["Body Mass Index"]["Weight lbs"].toFixed(1)} lbs{" "}
                        {homeData?.["Body Mass Index"]?.["Weight kg"] && (
                          <>({homeData["Body Mass Index"]["Weight kg"].toFixed(1)} kg)</>
                        )}
                      </>
                    ) : (
                      <>Weight: {homeData["Body Mass Index"]["Weight kg"].toFixed(1)} kg</>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Medication Reminders */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              Medication Reminders
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs font-medium text-foreground/70 hover:bg-accent hover:text-foreground"
              onClick={() =>
                navigate("/glucera" + GluceraNavigationRoutes.MEDICATIONS)
              }
            >
              Manage <ChevronRight className="ml-0.5 h-4 w-4" />
            </Button>
          </div>
        </Card>

        {/* Weekly Calorie Chart */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-foreground">
              Calorie Intake vs Burned
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Weekly comparison
            </p>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={weeklyCalorieData}
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                  interval={0}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, "auto"]}
                  width={30}
                  tickFormatter={value => `${value}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                    padding: "4px 8px",
                    minWidth: "120px",
                  }}
                  cursor={{
                    stroke: "hsl(var(--muted-foreground))",
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                  position={{ y: 0 }}
                />
                <Line
                  type="monotone"
                  dataKey="intake"
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  dot={{ fill: "#3b82f6", r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Intake"
                />
                <Line
                  type="monotone"
                  dataKey="burned"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={{ fill: "#10b981", r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Burned"
                />
                {/* Dashed Goal Line */}
                <ReferenceLine
                  y={mealGoals.calories_target}
                  stroke="hsl(var(--muted-foreground))"
                  strokeDasharray="3 3"
                  label={{
                    position: "top",
                    value: "Goal",
                    fill: "hsl(var(--muted-foreground))",
                    fontSize: 10,
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Weekly Sleep Chart */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Weekly Sleep
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Daily sleep hours
              </p>
            </div>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={weeklySleepData}
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                  interval={0}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 9]}
                  ticks={[0, 3, 6, 9]}
                  width={30}
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                  tickFormatter={value => `${value}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                    padding: "4px 8px",
                    minWidth: "120px",
                  }}
                  cursor={{
                    stroke: "hsl(var(--muted-foreground))",
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                  position={{ y: 0 }}
                  formatter={(value: any) => [
                    `${Number(value).toFixed(1)} hrs`,
                    "Sleep",
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="hours"
                  stroke="hsl(var(--info))"
                  strokeWidth={2.5}
                  dot={{ fill: "hsl(var(--info))", r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Sleep Hours"
                />
                {/* Baseline Dots */}
                <Line
                  dataKey="baseline"
                  stroke="none"
                  dot={{ fill: "#8b5cf6", r: 6, strokeWidth: 0 }} // Violet dots
                  activeDot={false}
                  tooltipType="none"
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Exercise Suggestions */}
        {/* <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-3.5 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              Suggested Workouts
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs font-medium text-foreground/70 hover:bg-accent hover:text-foreground"
              onClick={() =>
                navigate(
                  "/glucera" + GluceraNavigationRoutes.SUGGESTED_WORKOUTS
                )
              }
            >
              View All <ChevronRight className="ml-0.5 h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-2.5">
            <div className="flex items-center gap-3 rounded-lg border-2 border-border bg-muted/20 p-3.5">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border-2 border-orange-400 bg-orange-100">
                <Activity className="h-5 w-5 text-orange-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  Evening Walk
                </p>
                <p className="text-xs text-muted-foreground">
                  30 min • 200 cal
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs font-medium"
              >
                Start
              </Button>
            </div>
            <div className="flex items-center gap-3 rounded-lg border-2 border-border bg-muted/20 p-3.5">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border-2 border-blue-400 bg-blue-100">
                <TrendingUp className="h-5 w-5 text-blue-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  Strength Training
                </p>
                <p className="text-xs text-muted-foreground">
                  45 min • 320 cal
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs font-medium"
              >
                Start
              </Button>
            </div>
          </div>
        </Card> */}

        {/* Exercise Videos */}
        {/* <ExerciseVideosCard /> */}

        {/* Daily Insight */}
        {/* <DailyInsightCard insight={dailyInsight} /> */}

        {/* Quick Actions */}
        {/* <QuickActionsCard */}
        {/* onViewTrends={() => { */}
        {/* // navigate("/glucera" + GluceraNavigationRoutes.ACTIVITY_TRENDS) */}
        {/* }} */}
        {/* /> */}
      </div>

      <BottomNavBar />
    </div>
  );
};

export default HomePage;
